import React, { useEffect, useState } from 'react';
import { RocketLaunchIcon, ClockIcon, UserGroupIcon, ExclamationTriangleIcon, VideoCameraIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import { getResourceBySlug } from '../../../lib/db';

export default function KeyInfo() {
  const [resource, setResource] = useState(null);

  useEffect(() => {
    getResourceBySlug('we-fucked-up').then(setResource);
  }, []);

  return (
    <div className="space-y-8">
      <PlaybookHero
        title={resource?.title || "We F*cked Up"}
        subtitle={resource?.description || "A playbook for handling mistakes and building trust through transparency"}
        category={resource?.category || "Crisis Management"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type || ['Crisis', 'Communication', 'Trust']}
      />

      <PlaybookCard>
        <blockquote className="text-xl text-gray-200 italic border-l-4 border-primary-light pl-4 mb-6">
          "We've made a pretty material mistake, which is going to impact everyone who works here. Can you fix it and help manage the comms?"
          <footer className="text-sm text-gray-400 mt-2">— Startup leadership teams talking to Head of People…</footer>
        </blockquote>

        <h2 className="text-2xl font-semibold text-white mb-4">What Is This</h2>
        <p className="text-lg text-gray-200 mb-6">
          Welcome to your step-by-step guide on handling the aftermath of a f*ck up. It happens. People make mistakes, but how you manage things after is a make or break moment for your culture. This playbook is designed to help startup people leaders rebuild trust and reset after 'mistakes were made'.
        </p>
        <p className="text-lg text-gray-200">Let's get into it 🤘</p>
      </PlaybookCard>

      <ExpandableCallout
        title="This Playbook is for you if any of the following (or similar) are true"
        defaultOpen={true}
      >
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>The leadership team made a decision they wish they could take back</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>There's complaints about lack of transparency on decisions (high impact, high emotion)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Strategy was way off and we now have to backtrack and restructure</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>There was a poor executive hire that has been a disaster</span>
          </li>
        </ul>
      </ExpandableCallout>

      <PlaybookMetrics
        metrics={[
          {
            type: 'timeline',
            title: 'Timeline',
            value: '1-2 weeks',
            description: 'From initial mistake to full resolution and learning'
          },
          {
            type: 'size',
            title: 'Team Size',
            value: 'Any',
            description: 'Applicable to teams of any size'
          },
          {
            type: 'why',
            title: 'Why Use',
            value: 'Avoid disengagement and poor execution',
            description: 'Prevent low morale, disengagement, and people leaving'
          },
          {
            type: 'when',
            title: 'When to Use',
            value: 'Dealing with consequences',
            description: 'When handling the aftermath of a significant mistake'
          }
        ]}
      />

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <VideoCameraIcon className="h-6 w-6 text-primary-light mr-3" />
          <h2 className="text-xl font-semibold text-white">Watch This</h2>
        </div>
        <VideoEmbed
          src="https://www.loom.com/embed/0098f75148b64a99a06b0650c567898d?sid=38aa779c-739b-453e-bf2b-8a695d613c7b"
          title="Handling Mistakes in Your Organization"
          description="Learn how to effectively manage and communicate through organizational mistakes"
        />
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <ClipboardDocumentListIcon className="h-6 w-6 text-primary-light mr-3" />
          <h2 className="text-xl font-semibold text-white">What You'll Need</h2>
        </div>
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Leadership team support</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>A reasonably clear calendar for about 2 weeks</span>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <ExclamationTriangleIcon className="h-6 w-6 text-primary-light mr-3" />
          <h2 className="text-xl font-semibold text-white">Obstacles & How To Overcome Them</h2>
        </div>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Problem: Blame Culture Pervading the Team</h3>
            <p className="text-gray-300">
              <strong>Solution:</strong> Pair with exec team and ensure message is about directing emotion at the problem not the person(s) responsible.
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Problem: Rapid Loss of Trust from Team</h3>
            <p className="text-gray-300">
              <strong>Solution:</strong> Consistent transparent communication is key: what happened, why it happened, how you're addressing it, when you'll update again. The focus is on meeting commitments and communicating using multiple different mediums.
            </p>
          </div>
        </div>
      </PlaybookCard>
    </div>
  );
} 